import React, { useEffect, useState } from 'react';
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    FormGroup,
    Input,
    Label,
    Row,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook } from '@fortawesome/free-solid-svg-icons';
import { CATALOGO_OPERACIONES } from '../catalogos/catalogoOperaciones';
import { listPaises } from '../api/pldApi';
import ReactSelect from 'react-select';
import { alertarSiPaisGafi } from '../utils/paisesGafi';

const TIPOS_OPERACION = Object.keys(CATALOGO_OPERACIONES);

export default function FormExpediente({
    unidadId,
    asunto,
    tipoOperacion,
    solicitante,
    sociosRaiz,
    setAsunto,
    setTipoOperacion,
    setSolicitante,
    setSociosRaiz,
    esInmobiliaria,
}) {
    const [paises, setPaises] = useState([]);

    useEffect(() => {
        if (!unidadId) return undefined;
        let cancelled = false;
        async function cargar() {
            try {
                const data = await listPaises(unidadId);
                if (!cancelled && Array.isArray(data)) setPaises(data);
            } catch {
                if (!cancelled) setPaises([]);
            }
        }
        cargar();
        return () => {
            cancelled = true;
        };
    }, [unidadId]);

    const rolesPermitidos = CATALOGO_OPERACIONES[tipoOperacion] || [];
    const esMexico = solicitante.nacionalidad === 'México';
    const maxRfc = esMexico ? 13 : 50;

    function handleTipoOperacionChange(e) {
        const value = e.target.value;
        setTipoOperacion(value);
        setSociosRaiz([]);
        setSolicitante((s) => ({ ...s, caracter: '' }));
    }

    function handleSolicitanteField(field) {
        return function onChange(e) {
            const value =
                field === 'rfc' ? e.target.value.toUpperCase() : e.target.value;
            setSolicitante((s) => ({ ...s, [field]: value }));
        };
    }

    function handleCaracterChange(rolSeleccionado) {
        const limpiarEspec = rolSeleccionado !== 'OTRO';
        setSolicitante((s) => ({
            ...s,
            caracter: rolSeleccionado,
            otroEspecificacion: limpiarEspec ? '' : s.otroEspecificacion,
        }));
        if (!rolSeleccionado) return;

        const existe = sociosRaiz.find(
            (soc) =>
                soc.rfc &&
                solicitante.rfc &&
                soc.rfc.toUpperCase() === solicitante.rfc.toUpperCase()
        );
        if (!existe) {
            setSociosRaiz([
                ...sociosRaiz,
                {
                    id: String(Date.now()),
                    rolBase: rolSeleccionado,
                    otroEspecificacion: '',
                    tipo: 'Fisica',
                    nacionalidad: solicitante.nacionalidad || 'México',
                    pldNacionalidadClave: solicitante.pldNacionalidadClave || 'MEX',
                    nombre: solicitante.nombre,
                    paterno: solicitante.paterno,
                    materno: solicitante.materno,
                    rfc: solicitante.rfc,
                    acciones: '',
                },
            ]);
        } else {
            setSociosRaiz(
                sociosRaiz.map((s) =>
                    s.rfc &&
                    solicitante.rfc &&
                    s.rfc.toUpperCase() === solicitante.rfc.toUpperCase()
                        ? { ...s, rolBase: rolSeleccionado, otroEspecificacion: limpiarEspec ? '' : s.otroEspecificacion }
                        : s
                )
            );
        }
    }

    function handleOtroEspecificacionChange(value) {
        setSolicitante((s) => ({ ...s, otroEspecificacion: value }));
        // Sincronizar también en el socioRaiz correspondiente al solicitante
        setSociosRaiz((prev) =>
            prev.map((s) =>
                s.rfc &&
                solicitante.rfc &&
                s.rfc.toUpperCase() === solicitante.rfc.toUpperCase()
                    ? { ...s, otroEspecificacion: value }
                    : s
            )
        );
    }

    return (
        <Card className="mb-4 shadow-sm">
            <CardHeader className="border-bottom border-primary">
                <h5 className="mb-0 d-flex align-items-center">
                    <FontAwesomeIcon icon={faBook} className="text-primary mr-2 small" />
                    Datos del asunto y solicitante
                </h5>
            </CardHeader>
            <CardBody>
                {!unidadId ? (
                    <p className="text-muted small mb-3">
                        No se pudo determinar la unidad de negocio; el catálogo de
                        países no está disponible.
                    </p>
                ) : null}
                <Row className="mb-3">
                    <Col md="6">
                        <FormGroup>
                            <Label for="pldAsunto">Asunto *</Label>
                            <Input
                                id="pldAsunto"
                                type="text"
                                value={asunto}
                                onChange={(e) => setAsunto(e.target.value)}
                                placeholder="Ej. Operación Patito / Escritura 123"
                            />
                        </FormGroup>
                    </Col>
                    <Col md="6">
                        <FormGroup>
                            <Label for="pldTipoOperacion">
                                Acto jurídico a formalizar *
                            </Label>
                            <Input
                                id="pldTipoOperacion"
                                type="select"
                                value={tipoOperacion}
                                onChange={handleTipoOperacionChange}
                                className="font-weight-bold"
                            >
                                {TIPOS_OPERACION.map((acto) => (
                                    (!esInmobiliaria && acto === 'INMOBILIARIO') ? null : (
                                    <option key={acto} value={acto}>
                                        {acto}
                                    </option>
                                    )
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col md="2">
                        <FormGroup>
                            <Label for="pldSolNacionalidad">Nacionalidad *</Label>
                            <ReactSelect
                                inputId="pldSolNacionalidad"
                                placeholder="Seleccionar..."
                                value={
                                    solicitante.nacionalidad
                                        ? {
                                              value: solicitante.nacionalidad,
                                              label: solicitante.nacionalidad,
                                          }
                                        : null
                                }
                                onChange={(option) => {
                                    setSolicitante((s) => ({
                                        ...s,
                                        nacionalidad: option?.value || '',
                                        pldNacionalidadClave: option?.clave || '',
                                    }));
                                    alertarSiPaisGafi(option?.clave, option?.value);
                                }}
                                options={paises.map((pais) => ({
                                    value: pais.nombre,
                                    label: pais.nombre,
                                    clave: pais.clave,
                                }))}
                            />
                        </FormGroup>
                    </Col>
                    <Col md="3">
                        <FormGroup>
                            <Label for="pldSolNombre">Nombre(s) *</Label>
                            <Input
                                id="pldSolNombre"
                                type="text"
                                value={solicitante.nombre}
                                onChange={handleSolicitanteField('nombre')}
                            />
                        </FormGroup>
                    </Col>
                    <Col md="2">
                        <FormGroup>
                            <Label for="pldSolPaterno">Ap. paterno *</Label>
                            <Input
                                id="pldSolPaterno"
                                type="text"
                                value={solicitante.paterno}
                                onChange={handleSolicitanteField('paterno')}
                            />
                        </FormGroup>
                    </Col>
                    <Col md="2">
                        <FormGroup>
                            <Label for="pldSolMaterno">Ap. materno</Label>
                            <Input
                                id="pldSolMaterno"
                                type="text"
                                value={solicitante.materno}
                                onChange={handleSolicitanteField('materno')}
                            />
                        </FormGroup>
                    </Col>
                    <Col md="3">
                        <FormGroup>
                            <Label for="pldSolRfc">RFC *</Label>
                            <Input
                                id="pldSolRfc"
                                type="text"
                                value={solicitante.rfc}
                                onChange={handleSolicitanteField('rfc')}
                                maxLength={maxRfc}
                                className="text-uppercase"
                            />
                        </FormGroup>
                    </Col>
                </Row>

                <div className="bg-light p-3 rounded border mt-2">
                    <FormGroup>
                        <Label className="font-weight-bold">
                            ¿El solicitante comparece o firma en el instrumento?
                        </Label>
                        <Input
                            type="select"
                            value={solicitante.comparece}
                            onChange={handleSolicitanteField('comparece')}
                        >
                            <option value="">Seleccionar...</option>
                            <option value="no">
                                No, solo es el solicitante / contacto.
                            </option>
                            <option value="si">
                                Sí, comparece con un carácter específico.
                            </option>
                        </Input>
                    </FormGroup>

                    {solicitante.comparece === 'si' ? (
                        <FormGroup className="mb-0">
                            <Label
                                for="pldCaracter"
                                className="text-primary font-weight-bold small text-uppercase"
                            >
                                ¿Con qué carácter firma en la operación?
                            </Label>
                            <Input
                                id="pldCaracter"
                                type="select"
                                value={solicitante.caracter}
                                onChange={(e) =>
                                    handleCaracterChange(e.target.value)
                                }
                                className="border-primary bg-white font-weight-bold"
                            >
                                <option value="">Seleccionar...</option>
                                {rolesPermitidos.map((r) => (
                                    <option key={r} value={r}>
                                        {r}
                                    </option>
                                ))}
                            </Input>
                            {solicitante.caracter === 'OTRO' ? (
                                <FormGroup className="mt-3 mb-0">
                                    <Label
                                        for="pldOtroEspecificacion"
                                        className="text-primary font-weight-bold small text-uppercase"
                                    >
                                        Especificar carácter *
                                    </Label>
                                    <Input
                                        id="pldOtroEspecificacion"
                                        type="text"
                                        value={solicitante.otroEspecificacion || ''}
                                        onChange={(e) => handleOtroEspecificacionChange(e.target.value)}
                                        placeholder="Ej. Fideicomitente, Beneficiario, etc."
                                        className="border-primary"
                                    />
                                </FormGroup>
                            ) : null}
                            <p className="small text-muted mt-2 mb-0 font-italic">
                                Al seleccionar el carácter, su ficha se agregará
                                automáticamente en la sección de participantes.
                            </p>
                        </FormGroup>
                    ) : null}
                </div>
            </CardBody>
        </Card>
    );
}
